import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ActionButtons, { EditButton, DeleteButton } from '../../components/ActionButtons';
import GeriButon from '../../components/GeriButon';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import ModalShell from '../../components/ModalShell';

function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2 flex items-center gap-1">⚠️ {mesaj}</p>;
}

const toTitleCase = (str) => str ? str.toLocaleLowerCase('tr-TR').split(' ').map(word => word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1)).join(' ') : '';

function OdemeTipiForm({ kayit, onKaydet, onGeri, kaydediliyor, hata }) {
  const yeni = !kayit;
  const [ad, setAd] = useState(kayit?.ad ?? '');
  const [hatalar, setHatalar] = useState({});

  const INP = (hata) =>
    `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-[border-color,box-shadow,background-color] duration-200 bg-white focus:outline-none ${
      hata ? 'border-rose-400 bg-rose-50 focus:border-rose-500 shadow-rose-50 shadow-inner' : 'border-gray-100 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50 shadow-sm'
    }`;

  async function handleKaydet() {
    if (!ad.trim()) { setHatalar({ ad: 'Ödeme tipi adı zorunludur.' }); return; }
    onKaydet({ ...(kayit ? { odemeTipiId: kayit.odemeTipiId } : {}), ad: ad.trim() });
  }

  return (
    <ModalShell
      icon={yeni ? '➕' : '✏️'}
      iconColor="from-emerald-500 to-teal-600"
      title={yeni ? 'Yeni Ödeme Tipi' : `"${kayit.ad}" Düzenle`}
      subtitle="Kasa ve rezervasyon ödeme yöntemleri"
      onClose={onGeri}
      footer={
        <>
          <button onClick={onGeri} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 hover:border-gray-200 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-emerald-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ KAYDEDİLİYOR...' : '💾 TİPİ KAYDET'}
          </button>
        </>
      }
    >
      {(hata || hatalar.genel) && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-xs text-rose-600 font-black tracking-widest uppercase animate-shake">⚠️ {hata || hatalar.genel}</div>}

      <div>
        <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">Ödeme Yöntemi Adı *</label>
        <input value={ad} onChange={e => { setAd(toTitleCase(e.target.value)); setHatalar({}); }}
          className={INP(hatalar.ad)} placeholder="Örn: NAKİT, KREDİ KARTI, HAVALE" autoFocus onKeyDown={e => e.key === 'Enter' && handleKaydet()} />
        <FormHata mesaj={hatalar.ad} />
      </div>

      {ad.trim() && (
        <div className="bg-emerald-50/50 rounded-[2rem] p-6 border-2 border-emerald-100 border-dashed animate-fadeIn">
           <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mb-4 leading-none">Kasa / Fatura Görünümü</p>
           <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center text-white text-xl font-black shadow-xl shadow-emerald-100">
                 {ad.trim()[0].toUpperCase()}
              </div>
              <span className="font-black text-gray-800 text-lg tracking-widest truncate">{ad.trim()}</span>
           </div>
        </div>
      )}
    </ModalShell>
  );
}

export default function OdemeTipleri() {
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [view, setView] = useState('liste');
  const [secili, setSecili] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true); setHata(null);
    try { const data = await apiRequest('odemetipleri'); setListe(data || []); }
    catch { setHata('Veriler yüklenemedi.'); }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  async function kaydet(form) {
    setKaydediliyor(true); setHata(null);
    try {
      if (form.odemeTipiId) await apiRequest('odemetipleri', 'PUT', form);
      else await apiRequest('odemetipleri', 'POST', form);
      await veriYukle();
      setView('liste'); setSecili(null);
    } catch (e) {
      setHata(e?.message || 'Hata oluştu.');
    }
    setKaydediliyor(false);
  }

  async function handleSil(id) {
    try { await apiRequest(`odemetipleri/${id}`, 'DELETE'); setListe(p => p.filter(x => x.odemeTipiId !== id)); setSilOnay(null); }
    catch { alert('Hata oluştu.'); }
  }

  const filtrelenmis = liste.filter(x => x.ad?.toLocaleLowerCase('tr-TR').includes(aramaMetni.toLocaleLowerCase('tr-TR')));
  const ic = (kelime) => liste.filter(x => x.ad?.toLocaleLowerCase('tr-TR').includes(kelime.toLocaleLowerCase('tr-TR'))).length;

  const isOpen = view === 'ekle' || view === 'duzenle';

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="💳"
        iconColor="from-emerald-500 to-teal-700"
        iconShadow="shadow-emerald-200"
        title="Ödeme Tipleri"
        subtitle="Kasa ve rezervasyon ödeme yöntemleri"
      >
        <button onClick={() => { setSecili(null); setView('ekle'); }}
          className="group flex-shrink-0 flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-10 py-4 rounded-2xl text-[10px] font-black shadow-2xl shadow-emerald-100 transition-[background-color,transform] duration-300 hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden">
          <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl transition-transform group-hover:rotate-90">＋</span>
          <span className="relative font-black">YENİ ÖDEME TİPİ EKLE</span>
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'TOPLAM TİP', value: liste.length, icon: '💳', color: 'from-gray-700 to-gray-900 shadow-gray-200' },
          { label: 'NAKİT ÖDEME', value: ic('nakit'), icon: '💵', color: 'from-emerald-500 to-emerald-700 shadow-emerald-100' },
          { label: 'KREDİ KARTI', value: ic('kart'), icon: '💳', color: 'from-teal-500 to-teal-700 shadow-teal-100' },
          { label: 'BANKA / EFT', value: ic('hava') + ic('eft'), icon: '🏦', color: 'from-sky-500 to-indigo-700 shadow-sky-100' },
        ].map(k => (
          <div key={k.label} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-6 text-white shadow-xl hover:shadow-2xl transition-[box-shadow,transform] duration-300 group`}>
             <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-500">{k.icon}</div>
             <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none">{k.label}</p>
             <p className="text-2xl font-black tracking-tight leading-none">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-sm overflow-hidden min-h-[400px]">
        <div className="px-6 md:px-8 py-5 border-b border-gray-100 bg-gray-50/30 flex flex-col md:flex-row items-center gap-6">
           <div className="relative flex-1 w-full">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
              <input value={aramaMetni} onChange={e => setAramaMetni(e.target.value)} placeholder="Ödeme yöntemi ara..."
                className="w-full pl-11 pr-4 py-3.5 text-sm border-2 border-gray-100 rounded-2xl focus:border-emerald-400 focus:outline-none bg-white shadow-sm transition-all font-bold" />
           </div>
           <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-auto whitespace-nowrap">Sistemde toplam <b className="text-emerald-600 text-xs">{liste.length}</b> kayıt tanımlı</p>
        </div>

        {/* Mobile View */}
        <div className="md:hidden divide-y divide-gray-100 px-2 leading-none">
          {filtrelenmis.length === 0 ? (
            <EmptyState icon="💳" text="Kayıt Bulunamadı" />
          ) : filtrelenmis.map(item => (
            <div key={item.odemeTipiId} className="p-6 space-y-4 hover:bg-emerald-50/20 transition-all rounded-[2rem] group">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 border-2 border-white shadow-xl flex items-center justify-center text-white flex-shrink-0">
                    <span className="font-black text-lg uppercase tracking-widest">{item.ad?.[0]}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-black text-gray-800 text-base mb-1 tracking-tight truncate uppercase leading-none">{item.ad}</p>
                    <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1.5 opacity-70">💳 ÖDEME YÖNTEMİ</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                 <EditButton size="lg" onClick={() => { setSecili(item); setView('duzenle'); }} />
                 <DeleteButton size="lg" onClick={() => setSilOnay(item.odemeTipiId)} />
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto leading-none">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                <th className="py-5 px-8 text-left w-20">#</th>
                <th className="py-5 px-8 text-left">Ödeme Tipi Adı</th>
                <th className="py-5 px-8 text-right font-black">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtrelenmis.length === 0 ? (
                <EmptyState as="td" colSpan={3} icon="💳" text="Eşleşen sonuç bulunamadı" />
              ) : filtrelenmis.map((item, i) => (
                <tr key={item.odemeTipiId} className="border-b border-gray-50 group hover:bg-emerald-50/30 transition-colors duration-200">
                  <td className="py-5 px-8 font-black text-gray-300 text-xs">{i + 1}</td>
                  <td className="py-5 px-8">
                    <div className="flex items-center gap-5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white text-base font-black shadow-xl shadow-emerald-100 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                        {item.ad?.[0]}
                      </div>
                      <span className="font-black text-gray-800 tracking-tight uppercase text-lg">{item.ad}</span>
                    </div>
                  </td>
                  <td className="py-5 px-8 text-right font-black">
                    <div className="flex items-center justify-end gap-2 text-right leading-none">
                      <ActionButtons onEdit={() => { setSecili(item); setView('duzenle'); }} onDelete={() => setSilOnay(item.odemeTipiId)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UsageInfo 
        color="emerald"
        description="Ödeme Tipleri modülü, otelinizin tahsilat yaparken kullandığı tüm yöntemleri (Nakit, Kredi Kartı, Banka Transferi vb.) sisteme tanıtmanızı sağlar."
        scenarios={[
          { title: "Kasa Takibi ve Raporlama", content: "Farklı ödeme tipleri tanımlayarak gün sonunda kasanızın hangi yöntemlerle ne kadar tahsilat yaptığını kalem kalem görebilirsiniz." },
          { title: "Banka ve Komisyon Yönetimi", content: "Kredi kartı gibi ödeme tiplerini banka tanımları ile eşleştirerek, banka bazlı sliplerinizi ve POS komisyonlarınızı daha kolay takip edebilirsiniz." },
          { title: "Misafir Folio İşlemleri", content: "Checkout sırasında misafirin ücretini nasıl ödediğini belirleyerek fatura ve e-arşiv süreçlerinde yasal zorunlulukları yerine getirmiş olursunuz." }
        ]}
      />

      {isOpen && createPortal(
        <OdemeTipiForm 
          kayit={secili} 
          onKaydet={kaydet} 
          onGeri={() => { setView('liste'); setSecili(null); }} 
          kaydediliyor={kaydediliyor}
          hata={hata}
        />,
        document.body
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center px-4 backdrop-blur-md animate-fade-in shadow-2xl">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm text-center border border-gray-100">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight uppercase leading-none">Ödeme Tipini Sil</h3>
            <p className="text-[11px] font-black text-gray-500 mt-5 mb-10 leading-relaxed uppercase tracking-tight">
               <b className="text-gray-900">"{liste.find(x => x.odemeTipiId === silOnay)?.ad}"</b> yöntemi kalıcı olarak silinecek. Emin misiniz?
            </p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all uppercase tracking-widest active:scale-95">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}